import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Calendar, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwbGl2ZSUyMHN0cmVhbSUyMHBlcmZvcm1hbmNlfGVufDB8fHx8MTc4MjE2ODg5NXww&ixlib=rb-4.1.0&q=85";

const MIN_AGE = 18;

// Full years between a date-of-birth string (YYYY-MM-DD) and today.
const ageFromDob = (dob) => {
  if (!dob) return null;
  const b = new Date(dob);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
};

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", dob: "" });
  const [ageConfirm, setAgeConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();

    // Age gate (frontend enforcement): WhooLive is 18+ due to camera, microphone
    // and live streaming. Block under-age sign-ups before hitting the backend.
    const age = ageFromDob(form.dob);
    if (age === null) {
      toast.error("Please enter your date of birth.");
      return;
    }
    if (age < MIN_AGE) {
      toast.error("You must be at least 18 years old to use WhooLive.", {
        description: "Registration is not available for users under 18.",
      });
      return;
    }
    if (!ageConfirm) {
      toast.error(`Please confirm you are at least ${MIN_AGE} years old.`);
      return;
    }

    setBusy(true);
    try {
      const { name, email, password } = form;
      const u = await register({ name, email, password });
      toast.success(`Welcome to WhooLive, ${u.name}`);
      navigate("/");
    } catch (err) {
      toast.error("Registration failed", {
        description: err.detail?.detail || err.message || "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Visual */}
      <div className="relative hidden lg:block">
        <img src={SIDE_IMG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight">
            Create. Go live. Bring people together.
          </h2>
          <p className="mt-3 max-w-md text-white/80">
            Sign up to host events and stream to your community.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="mb-10 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="mb-8">
            <Logo size={44} />
          </div>

          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
            Create your account
          </h1>
          <p className="mt-2 text-muted-foreground">
            Join WhooLive to discover and host live moments.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} placeholder="Maya Rivers" className="h-12 rounded-xl pl-10" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="email" type="email" data-testid="register-email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="h-12 rounded-xl pl-10" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" type="password" data-testid="register-password" value={form.password} onChange={set("password")} placeholder="Create a password" className="h-12 rounded-xl pl-10" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Date of birth</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="dob"
                  type="date"
                  data-testid="register-dob"
                  value={form.dob}
                  onChange={set("dob")}
                  max={new Date().toISOString().split("T")[0]}
                  className="h-12 rounded-xl pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                You must be at least 18 years old to use WhooLive.
              </p>
            </div>

            <label
              htmlFor="age-confirm"
              className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground"
            >
              <Checkbox
                id="age-confirm"
                data-testid="register-age-confirm"
                checked={ageConfirm}
                onCheckedChange={(v) => setAgeConfirm(!!v)}
                className="mt-0.5"
              />
              <span>
                I confirm that I am at least 18 years old and I accept the{" "}
                <Link to="/privacy" data-testid="register-privacy-link" className="font-semibold text-brand hover:underline">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            <Button
              type="submit"
              data-testid="register-submit"
              disabled={busy}
              className="h-12 w-full rounded-full bg-brand text-base hover:bg-brand-hover"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-brand" data-testid="register-to-login">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
